import date from 'date-and-time';
import { AppTarget, Config, Environment, Platform } from './types';
import { ReleaseChannel, ReleaseChannelApp } from '../types/releaseChannel';
import { getLocalIP } from './ipAddress';
import { getFinalDayOfAnniversary, getStartDayOfAnniversary } from '../anniversary/anniversaryDates';

export const appConfig: Config = (platform, target, environment) => {
  const localIp = getLocalIP(environment);
  const myIpAddressOrLocalhost =
    localIp !== undefined && (platform === Platform.ios || platform === Platform.android) ? localIp : 'localhost';
  console.log(`%cYour Local IP Address: ${myIpAddressOrLocalhost}`, 'background: #6417fb; color: white;');
  console.log(`Preparing common config... platform: ${platform}, target: ${target}, env: ${environment}`);
  const appleSubscriptionManagementSupportURL = 'https://support.apple.com/en-us/HT202039';
  const googlePlaySubscriptionManagementSupportURL = 'https://support.google.com/googleplay/answer/7018481';

  const canUseRelativeAPIUrl =
    platform !== Platform.ios &&
    platform !== Platform.android &&
    platform !== Platform.server &&
    (platform !== Platform.electron ||
      environment === Environment.local ||
      environment === Environment.localProduction); // on Electron for local development, we use devProxy server, to avoid CORS issues

  if (environment === Environment.production || environment === Environment.localProduction) {
    const relativeApiURL = environment === Environment.localProduction ? 'api_productionapi' : 'api_api';
    const relativeMusicApiURL = environment === Environment.localProduction ? 'api_productionmusicapi' : 'api_musicapi';
    const protocolSchemaFYM = environment === Environment.localProduction ? 'fymlocal' : 'fym';
    return {
      fymApiURL: (supportsCORS) =>
        supportsCORS && canUseRelativeAPIUrl ? `/${relativeApiURL}/` : 'https://api.freeyourmusic.com/',
      socketUrl: `https://api.freeyourmusic.com/`,
      musicApiURL: (supportsCORS) =>
        supportsCORS && canUseRelativeAPIUrl ? `/${relativeMusicApiURL}/` : 'https://api.musicapi.com/',
      musicAppURL: 'https://app.musicapi.com/',
      musicApiToken: '7dadaf8f-6297-4e35-9661-d007ea65696c',
      fymWebURL: 'https://freeyourmusic.com/',
      smartLinksWebURL: 'https://music24.com/',
      boomplayAppId: 'freeyourmusic',
      amazonSecurityProfileId: 'amzn1.application.976844aef49644b5b495f6563f5ea1a5',
      loadLocalRealm: false,
      appDomains: target === AppTarget.smartlinks ? ['music24.com'] : ['fym.fm', 'freeyourmusic.com'],
      protocolSchema: target === AppTarget.smartlinks ? 'music24' : protocolSchemaFYM,
      smartLinkAppUrl: `https://music24.com/app`,
      smartLinkCookieDomain: `music24.com`,
      appReleaseLinks: {
        channel: ReleaseChannel.production,
        app: target === AppTarget.smartlinks ? ReleaseChannelApp.smartlinks : ReleaseChannelApp.freeyourmusic,
      },
      environment,
      target,
      platform,
      appleSubscriptionManagementSupportURL,
      googlePlaySubscriptionManagementSupportURL,
      shouldShowAnniversary: () => {
        const currentDate = new Date();
        return (
          currentDate.getTime() >= getStartDayOfAnniversary().getTime() &&
          currentDate.getTime() <= getFinalDayOfAnniversary().getTime()
        );
      },
      meilisearchHost: 'https://search.musicapi.com',
      meilisearchApiKey: '169d27389ceb46b88d9e6c4b2e086c9dc911ab16d001a70425d0dc84cd80634e',
      imageProcessingURL: 'https://imgproxy.hernas.com/',
      imageStorageBaseURL: 'https://s3.nl-ams.scw.cloud/musicapi-data/',
      imageStaticsBaseURL: 'https://statics.freeyourmusic.com',
    };
  }
  if (environment === Environment.staging) {
    return {
      fymApiURL: (supportsCORS) => (supportsCORS && canUseRelativeAPIUrl ? '/api_api/' : 'https://api.stampapp.io/'),
      socketUrl: `https://api.stampapp.io/`,
      musicApiURL: (supportsCORS) =>
        supportsCORS && canUseRelativeAPIUrl ? `/api_musicapi/` : 'https://api.musicmigrator.com/',
      musicAppURL: 'https://app.musicmigrator.com/',
      musicApiToken: '8d883178-9585-482e-bb4b-0c85736edc2d',
      fymWebURL: 'https://site.stampapp.io/',
      smartLinksWebURL: 'https://site.musicpawa.com/',
      boomplayAppId: 'freeyourmusic',
      amazonSecurityProfileId: 'amzn1.application.976844aef49644b5b495f6563f5ea1a5',
      loadLocalRealm: false,
      appDomains: target === AppTarget.smartlinks ? ['site.musicpawa.com'] : ['site.stampapp.io', 'site2.stampapp.io'],
      protocolSchema: target === AppTarget.smartlinks ? 'music24rc' : 'fymrc',
      appReleaseLinks: {
        channel: ReleaseChannel.staging,
        app: target === AppTarget.smartlinks ? ReleaseChannelApp.smartlinks : ReleaseChannelApp.freeyourmusic,
      },
      smartLinkAppUrl: `https://site.musicpawa.com/app`,
      smartLinkCookieDomain: `musicpawa.com`,
      environment,
      target,
      platform,
      appleSubscriptionManagementSupportURL,
      googlePlaySubscriptionManagementSupportURL,
      shouldShowAnniversary: () => {
        const currentDate = new Date();
        const startDay = date.addDays(getStartDayOfAnniversary(), -30);
        return (
          currentDate.getTime() >= startDay.getTime() && currentDate.getTime() <= getFinalDayOfAnniversary().getTime()
        );
      },
      meilisearchHost: 'https://search.musicmigrator.com',
      meilisearchApiKey: 'df2a4c6ee291238063d32295bccf8f57037e4dacaf6c1bb5fdbd6e3a501d42b2',
      imageProcessingURL: 'https://imgproxy.sanreh.com/',
      imageStorageBaseURL: 'https://s3.nl-ams.scw.cloud/musicapi-staging-data/',
      imageStaticsBaseURL: 'https://statics.freeyourmusic.com',
    };
  }
  const serverPort = process.env.SERVER_PORT || '3223';
  // Music24's API was split out of server/. The smartlinks target talks to it;
  // FreeYourMusic keeps talking to server/. Both share the same databases.
  const music24ApiPort = process.env.MUSIC24_API_PORT || '3224';
  const apiPort = target === AppTarget.smartlinks ? music24ApiPort : serverPort;
  const musicapiPort = process.env.MUSICAPI_PORT || '4223';
  const musicapiWebPort = process.env.MUSICAPI_WEB_PORT || '4020';
  const fymWebsitePort = process.env.FYM_WEBSITE_PORT || '3010';
  const music24WebsitePort = process.env.MUSIC24_WEBSITE_PORT || '3012';
  const webSmlPort = process.env.WEB_SML_PORT || '3002';
  const meilisearchPort = process.env.MUSICAPI_MEILISEARCH_PORT || '7785';
  // MUSICAPI_IMAGINARY_PORT is still honoured so existing local .env files
  // keep working after the rename to imgproxy.
  const imgproxyPort = process.env.MUSICAPI_IMGPROXY_PORT || process.env.MUSICAPI_IMAGINARY_PORT || '9800';
  const s3Port = process.env.MUSICAPI_S3_PORT || '9091';

  return {
    fymApiURL: (supportsCORS) =>
      supportsCORS && canUseRelativeAPIUrl ? '/api_api/' : `http://${myIpAddressOrLocalhost}:${apiPort}/`,
    socketUrl: `http://${myIpAddressOrLocalhost}:${apiPort}/`,
    musicApiURL: (supportsCORS) =>
      supportsCORS && canUseRelativeAPIUrl ? '/api_musicapi/' : `http://${myIpAddressOrLocalhost}:${musicapiPort}/`,
    musicAppURL: `http://${myIpAddressOrLocalhost}:${musicapiWebPort}/`,
    musicApiToken: '8d883178-9585-482e-bb4b-0c85736edc2d',
    fymWebURL: `http://${myIpAddressOrLocalhost}:${fymWebsitePort}/`,
    smartLinksWebURL: `http://${myIpAddressOrLocalhost}:${music24WebsitePort}/`,
    boomplayAppId: 'freeyourmusic',
    amazonSecurityProfileId: 'amzn1.application.976844aef49644b5b495f6563f5ea1a5',
    loadLocalRealm: false,
    appDomains: target === AppTarget.smartlinks ? ['site.musicpawa.com'] : ['site.stampapp.io', 'site2.stampapp.io'],
    protocolSchema: target === AppTarget.smartlinks ? 'music24local' : 'fymlocal',
    smartLinkAppUrl: `http://${myIpAddressOrLocalhost}:${webSmlPort}/app`,
    smartLinkCookieDomain: myIpAddressOrLocalhost,
    appReleaseLinks: {
      channel: ReleaseChannel.staging,
      app: target === AppTarget.smartlinks ? ReleaseChannelApp.smartlinks : ReleaseChannelApp.freeyourmusic,
    },
    environment,
    target,
    platform,
    appleSubscriptionManagementSupportURL,
    googlePlaySubscriptionManagementSupportURL,
    shouldShowAnniversary: () => {
      return false;
    },
    meilisearchHost: `http://${myIpAddressOrLocalhost}:${meilisearchPort}`,
    meilisearchApiKey: 'HHP8g9nRJAFAu7SWAn0wV2tfp32xu54ymJ5xxHMtXi0',
    imageProcessingURL: `http://127.0.0.1:${imgproxyPort}/`,
    imageStorageBaseURL: `http://localhost:${s3Port}/fym-data-local/`,
    imageStaticsBaseURL: `http://${myIpAddressOrLocalhost}:${fymWebsitePort}`,
  };
};
